import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import warehouseService, { type Warehouse } from "@/services/warehouse";
import settingService from "@/services/setting";
import productService, { type Product, type StockEntry } from "@/services/product";
import adjustmentService, {
  createAdjustment,
  deleteAdjustment,
  getAdjustment,
  getAdjustments,
  type Adjustment,
} from "@/services/adjustment";
import { useCurrentUser } from "@/composables/useCurrentUser";

type AdjustmentProduct = Product & {
  quantity: number;
  adjustmentType: "add" | "subtract";
  availableStock: number;
  originalQuantity: number;
  originalType: "add" | "subtract";
};

type AdjustmentFormState = {
  warehouse_id: number | null;
  ref: string;
  date: string;
  note: string;
};

type AdjustmentApiItem = {
  product_id?: number;
  quantity?: number;
  type?: "add" | "subtract";
  cost?: number;
  product?: Product;
};

const normalizeSearch = (value: string) => value.replace(/\s+/g, " ").trim();
const toNumber = (value?: number | string | null) => Number(value ?? 0);

const createInitialForm = (): AdjustmentFormState => ({
  warehouse_id: null,
  ref: "",
  date: new Date().toISOString().split("T")[0],
  note: "",
});

export function useAdjustmentList() {
  const { t } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();
  const searchQuery = ref("");
  const warehouses = ref<{ id?: number; name?: string }[]>([]);
  const detailsLoading = ref(false);
  const loading = ref(false);
  const profileWarehouseId = ref<number>(0);
  const selectedAdjustment = ref<Adjustment | null>(null);
  const adjustments = ref<Adjustment[]>([]);
  const filters = reactive({
    warehouse_id: "",
  });
  const activeFilters = ref({
    warehouse_id: "",
  });

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const initializeWarehouseContext = async () => {
    const currentUser = await fetchCurrentUser(true);
    const warehouseId = String(currentUser?.warehouse_id ?? "").trim();

    if (warehouseId) {
      profileWarehouseId.value = Number(warehouseId);
      filters.warehouse_id = warehouseId;
      activeFilters.value.warehouse_id = warehouseId;
    }
  };

  const fetchAdjustmentsList = async () => {
    loading.value = true;
    try {
      const response = await getAdjustments({
        search: searchQuery.value,
        ...activeFilters.value,
      });
      adjustments.value = response.data;
    } catch (error) {
      console.error("Error fetching adjustments:", error);
      Swal.fire({
        icon: "error",
        title: t("adjustment_page.failed_to_load"),
        text: t("adjustment_page.unable_to_load_stock_adjustments"),
      });
    } finally {
      loading.value = false;
    }
  };

  const applyFilters = () => {
    activeFilters.value = { ...filters };
    fetchAdjustmentsList();
  };

  const resetFilters = () => {
    filters.warehouse_id = profileWarehouseId.value ? String(profileWarehouseId.value) : "";
    applyFilters();
  };

  const handleViewDetails = async (adjustment: Adjustment) => {
    if (!adjustment?.id) {
      selectedAdjustment.value = adjustment;
      return;
    }

    detailsLoading.value = true;
    try {
      selectedAdjustment.value = await getAdjustment(adjustment.id);
    } catch (error) {
      console.error("Error loading adjustment details:", error);
      selectedAdjustment.value = adjustment;
    } finally {
      detailsLoading.value = false;
    }
  };

  const handleDeleteAdjustment = async (adjustment: Adjustment) => {
    if (!adjustment.id) return;

    const result = await Swal.fire({
      title: t("adjustment_page.delete_adjustment_title"),
      text: t("adjustment_page.delete_adjustment_text", { reference: adjustment.ref }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("adjustment_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("adjustment_page.deleting"),
        text: t("adjustment_page.please_wait"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await deleteAdjustment(adjustment.id);
      await fetchAdjustmentsList();

      Swal.fire({
        icon: "success",
        title: t("adjustment_page.deleted_title"),
        text: t("adjustment_page.adjustment_deleted_successfully"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting adjustment:", error);
      Swal.fire({
        icon: "error",
        title: t("adjustment_page.delete_failed"),
        text: t("adjustment_page.unable_to_delete_adjustment"),
      });
    }
  };

  watch(searchQuery, fetchAdjustmentsList);

  onMounted(async () => {
    await Promise.all([initializeWarehouseContext(), fetchWarehouses()]);
    await fetchAdjustmentsList();
  });

  return {
    searchQuery,
    warehouses,
    detailsLoading,
    loading,
    profileWarehouseId,
    filters,
    activeFilters,
    selectedAdjustment,
    adjustments,
    applyFilters,
    resetFilters,
    handleViewDetails,
    handleDeleteAdjustment,
  };
}

function buildAdjustmentForm(mode: "create" | "edit") {
  const { t } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();
  const route = useRoute();
  const router = useRouter();
  const breadcrumbItems =
    mode === "edit"
      ? [
          { label: "adjustment_list", to: "/adjustment-list" },
          { label: "edit_adjustment" },
        ]
      : [
          { label: "adjustment_list", to: "/adjustment-list" },
          { label: "create_adjustment" },
        ];

  const warehouses = ref<Warehouse[]>([]);
  const selectedProducts = ref<AdjustmentProduct[]>([]);
  const searchResults = ref<AdjustmentProduct[]>([]);
  const showResults = ref(false);
  const isSubmitting = ref(false);
  const isLoadingData = ref(false);
  const profileWarehouseId = ref<number | null>(null);
  const form = reactive<AdjustmentFormState>(createInitialForm());
  const adjustmentId = computed(() => Number(route.params.id || 0));
  const warehouseLockedToProfile = computed(() => profileWarehouseId.value !== null);

  const getWarehouseStock = (stocks?: StockEntry[], warehouseId?: number | null) => {
    if (!stocks?.length || !warehouseId) return 0;

    return stocks
      .filter((stock) => Number(stock.warehouse_id) === Number(warehouseId))
      .reduce((total, stock) => total + toNumber(stock.qty ?? stock.quantity), 0);
  };

  const toAdjustmentProduct = (
    product: Product,
    options?: { quantity?: number; type?: "add" | "subtract"; cost?: number }
  ): AdjustmentProduct => ({
    ...product,
    quantity: Math.max(1, Number(options?.quantity ?? 1)),
    adjustmentType: options?.type ?? "add",
    cost: options?.cost ?? product.cost,
    availableStock: getWarehouseStock(product.stocks, form.warehouse_id),
    originalQuantity: Number(options?.quantity ?? 0),
    originalType: options?.type ?? "add",
  });

  const getEditableStock = (currentStock: number, product: AdjustmentProduct) => {
    if (mode !== "edit") return currentStock;

    if (product.originalType === "subtract") {
      return currentStock + product.originalQuantity;
    }

    if (product.originalType === "add") {
      return Math.max(0, currentStock - product.originalQuantity);
    }

    return currentStock;
  };

  const refreshSelectedProductStocks = async () => {
    if (!form.warehouse_id || selectedProducts.value.length === 0) {
      selectedProducts.value = selectedProducts.value.map((product) => ({
        ...product,
        availableStock: 0,
      }));
      return;
    }

    const refreshedProducts = await Promise.all(
      selectedProducts.value.map(async (selectedProduct) => {
        if (!selectedProduct.id) {
          return {
            ...selectedProduct,
            availableStock: 0,
          };
        }

        try {
          const latestProduct = await productService.getProduct(selectedProduct.id);
          const currentStock = getWarehouseStock(latestProduct.stocks, form.warehouse_id);
          const availableStock = getEditableStock(currentStock, selectedProduct);
          const nextQuantity =
            selectedProduct.adjustmentType === "subtract" && availableStock > 0
              ? Math.min(selectedProduct.quantity, availableStock)
              : selectedProduct.quantity;

          return {
            ...selectedProduct,
            ...latestProduct,
            availableStock,
            quantity: Math.max(1, nextQuantity),
          };
        } catch (error) {
          console.error(`Failed to refresh adjustment stock for product ${selectedProduct.id}:`, error);
          return {
            ...selectedProduct,
            availableStock: 0,
          };
        }
      })
    );

    selectedProducts.value = refreshedProducts;
  };

  const fetchBaseData = async () => {
    const [warehouseRes, settingsRes, currentUser] = await Promise.allSettled([
      warehouseService.getWarehouses(),
      settingService.getSettings(),
      fetchCurrentUser(true),
    ]);

    if (warehouseRes.status === "fulfilled") {
      warehouses.value = warehouseRes.value;
    }

    const currentUserWarehouseId =
      currentUser.status === "fulfilled" && currentUser.value?.warehouse_id
        ? Number(currentUser.value.warehouse_id)
        : null;

    profileWarehouseId.value = currentUserWarehouseId;

    if (currentUserWarehouseId) {
      form.warehouse_id = currentUserWarehouseId;
    } else if (settingsRes.status === "fulfilled" && settingsRes.value?.default_warehouse) {
      form.warehouse_id = Number(settingsRes.value.default_warehouse);
    } else if (warehouses.value.length > 0) {
      form.warehouse_id = Number(warehouses.value[0].id ?? null);
    }
  };

  const fetchCreateDefaults = async () => {
    form.ref = await adjustmentService.getNextReference();
  };

  const fetchAdjustmentDetails = async () => {
    if (!adjustmentId.value) {
      throw new Error(t("adjustment_page.adjustment_id_missing"));
    }

    const adjustment = await getAdjustment(adjustmentId.value);

    form.warehouse_id = Number(adjustment.warehouse_id ?? null);
    form.ref = adjustment.ref || "";
    form.date = adjustment.date || adjustment.created_at?.split("T")[0] || form.date;
    form.note = adjustment.note || "";

    const items = Array.isArray(adjustment.items) ? (adjustment.items as AdjustmentApiItem[]) : [];
    selectedProducts.value = items
      .filter((item) => item.product && item.product_id)
      .map((item) =>
        toAdjustmentProduct(item.product as Product, {
          quantity: item.quantity,
          type: item.type,
          cost: item.cost,
        })
      );
  };

  const initializePage = async () => {
    isLoadingData.value = true;

    try {
      await fetchBaseData();

      if (mode === "edit") {
        await fetchAdjustmentDetails();
        await refreshSelectedProductStocks();
      } else {
        await fetchCreateDefaults();
      }
    } catch (error) {
      console.error(`Failed to initialize ${mode} adjustment page:`, error);
      await Swal.fire({
        icon: "error",
        title: t("adjustment_page.failed_to_load"),
        text: t("adjustment_page.unable_to_load_adjustment_page", {
          mode: mode === "edit" ? t("edit_adjustment").toLowerCase() : t("create_adjustment").toLowerCase(),
        }),
      });
      router.push("/adjustment-list");
    } finally {
      isLoadingData.value = false;
    }
  };

  const handleProductSearch = async (query: string) => {
    const normalizedQuery = normalizeSearch(query);

    if (!form.warehouse_id) {
      searchResults.value = [];
      showResults.value = false;
      return;
    }

    try {
      const products = await productService.getProducts({
        type: 1,
        warehouse_id: form.warehouse_id || undefined,
        search: normalizedQuery || undefined,
      });

      searchResults.value = products.map((product) => toAdjustmentProduct(product));
      showResults.value = true;
    } catch (error) {
      console.error("Error searching products:", error);
      searchResults.value = [];
      showResults.value = false;
    }
  };

  const addProduct = (product: Product) => {
    if (Number(product.type ?? 1) !== 1) {
      return;
    }

    const adjustmentProduct = toAdjustmentProduct(product);
    const existing = selectedProducts.value.find((p) => p.id === product.id);

    if (existing) {
      existing.quantity += 1;
      existing.availableStock = adjustmentProduct.availableStock;
    } else {
      selectedProducts.value.push(adjustmentProduct);
    }

    showResults.value = false;
  };

  const removeProduct = (index: number) => {
    selectedProducts.value.splice(index, 1);
  };

  const updateProductQuantity = (index: number, quantity: number) => {
    const product = selectedProducts.value[index];
    if (!product) return;

    const safeQuantity = Math.max(1, quantity);
    if (product.adjustmentType === "subtract" && product.availableStock > 0) {
      product.quantity = Math.min(safeQuantity, product.availableStock);
    } else {
      product.quantity = safeQuantity;
    }
  };

  const updateProductType = (index: number, type: "add" | "subtract") => {
    const product = selectedProducts.value[index];
    if (!product) return;

    product.adjustmentType = type;
    if (type === "subtract" && product.availableStock > 0 && product.quantity > product.availableStock) {
      product.quantity = product.availableStock;
    }
  };

  const buildPayload = () => {
    const selectedWarehouse = warehouses.value.find((w) => w.id === form.warehouse_id);

    return {
      warehouse_id: Number(form.warehouse_id),
      warehouse_code: selectedWarehouse?.code || "",
      warehouse_name: selectedWarehouse?.name || "",
      ref: form.ref,
      date: form.date,
      note: form.note,
      items: selectedProducts.value
        .filter((product): product is AdjustmentProduct & { id: number } => typeof product.id === "number")
        .map((p) => ({
          product_id: p.id,
          product_code: p.code,
          product_name: p.name,
          quantity: p.quantity,
          type: p.adjustmentType,
          cost: p.cost,
        })),
    };
  };

  const handleSubmit = async () => {
    if (!form.ref) {
      await Swal.fire({
        icon: "warning",
        title: t("adjustment_page.reference_required"),
        text: t("adjustment_page.reference_no_required"),
      });
      return;
    }

    if (!form.warehouse_id || selectedProducts.value.length === 0) {
      await Swal.fire({
        icon: "warning",
        title: t("adjustment_page.incomplete_adjustment"),
        text: t("adjustment_page.select_warehouse_and_product"),
      });
      return;
    }

    const invalidDeduction = selectedProducts.value.find(
      (product) => product.adjustmentType === "subtract" && product.quantity > product.availableStock
    );
    const invalidProduct = selectedProducts.value.find((product) => !product.id);

    if (invalidDeduction) {
      await Swal.fire({
        icon: "warning",
        title: t("adjustment_page.invalid_deduction"),
        text: t("adjustment_page.exceeds_available_stock_in_selected_warehouse", {
          name: invalidDeduction.name,
        }),
      });
      return;
    }

    if (invalidProduct) {
      await Swal.fire({
        icon: "error",
        title: t("adjustment_page.invalid_product"),
        text: t("adjustment_page.selected_product_missing_valid_id"),
      });
      return;
    }

    isSubmitting.value = true;
    try {
      Swal.fire({
        title: mode === "edit" ? t("adjustment_page.updating_adjustment") : t("adjustment_page.saving_adjustment"),
        text:
          mode === "edit"
            ? t("adjustment_page.update_stock_adjustment_wait")
            : t("adjustment_page.create_stock_adjustment_wait"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const payload = buildPayload();

      if (mode === "edit") {
        await adjustmentService.updateAdjustment(adjustmentId.value, payload);
      } else {
        await createAdjustment(payload);
      }

      await Swal.fire({
        icon: "success",
        title: mode === "edit" ? t("adjustment_page.adjustment_updated") : t("adjustment_page.adjustment_created"),
        text:
          mode === "edit"
            ? t("adjustment_page.adjustment_updated_successfully")
            : t("adjustment_page.adjustment_created_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });

      router.push("/adjustment-list");
    } catch (error: any) {
      console.error(`Error ${mode}ing adjustment:`, error);
      await Swal.fire({
        icon: "error",
        title: mode === "edit" ? t("adjustment_page.update_failed") : t("adjustment_page.create_failed"),
        text: error?.response?.data?.message || (
          mode === "edit"
            ? t("adjustment_page.failed_to_update_adjustment")
            : t("adjustment_page.failed_to_create_adjustment")
        ),
      });
    } finally {
      isSubmitting.value = false;
    }
  };

  watch(
    () => form.warehouse_id,
    async (newWarehouseId, oldWarehouseId) => {
      if (newWarehouseId === oldWarehouseId) return;
      searchResults.value = [];
      showResults.value = false;
      await refreshSelectedProductStocks();
    }
  );

  onMounted(initializePage);

  return {
    breadcrumbItems,
    warehouses,
    selectedProducts,
    searchResults,
    showResults,
    isSubmitting,
    isLoadingData,
    profileWarehouseId,
    form,
    warehouseLockedToProfile,
    handleProductSearch,
    addProduct,
    removeProduct,
    updateProductQuantity,
    updateProductType,
    handleSubmit,
  };
}

export function useAdjustmentForm() {
  return buildAdjustmentForm("create");
}

export function useAdjustmentEditForm() {
  return buildAdjustmentForm("edit");
}
